import { cloneElement, Fragment, isValidElement, type ReactNode } from 'react';

import { normalizePhrase, normalizeWithIndexMap } from '@/lib/textMatch';

/**
 * Recursively wrap occurrences of `phrases` inside React text nodes with
 * `<mark>` elements. Used to paint known highlight passages over rendered
 * article markdown.
 *
 * This is best-effort: a phrase split across element boundaries (e.g. partly
 * inside `<strong>`) won't match, since only single text nodes are searched.
 */

/** Normalize a raw phrase list into something safe to match against. */
export function normalizePhrases(phrases: string[], max = 50): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const phrase of phrases) {
    const trimmed = normalizePhrase(phrase);
    // Very short phrases produce noisy, meaningless matches.
    if (trimmed.length < 3 || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
    if (result.length >= max) break;
  }

  // Longest first so overlapping phrases match greedily.
  return result.sort((a, b) => b.length - a.length);
}

function markString(text: string, phrases: string[]): ReactNode {
  // Whitespace-normalize the haystack once, keeping offsets into `text`.
  const { normalized, indexMap } = normalizeWithIndexMap(text);

  // Find the earliest match among all phrases.
  let bestIndex = -1;
  let bestPhrase = '';

  for (const phrase of phrases) {
    const index = normalized.indexOf(phrase);
    if (index !== -1 && (bestIndex === -1 || index < bestIndex)) {
      bestIndex = index;
      bestPhrase = phrase;
    }
  }

  if (bestIndex === -1) return text;

  // Map the normalized match back to offsets in the original text.
  const start = indexMap[bestIndex];
  const end = indexMap[bestIndex + bestPhrase.length - 1] + 1;

  const before = text.slice(0, start);
  const match = text.slice(start, end);
  const after = text.slice(end);

  return (
    <Fragment key={start}>
      {before && markString(before, phrases)}
      <mark className="hl-mark">{match}</mark>
      {after && markString(after, phrases)}
    </Fragment>
  );
}

/** Deep-map React children, marking phrase matches in every text node. */
export function markify(node: ReactNode, phrases: string[]): ReactNode {
  if (phrases.length === 0) return node;

  if (typeof node === 'string') {
    return markString(node, phrases);
  }

  if (Array.isArray(node)) {
    return node.map((child, index) => <Fragment key={index}>{markify(child, phrases)}</Fragment>);
  }

  if (isValidElement<{ children?: ReactNode }>(node) && node.props.children) {
    // Don't mark inside code or links — visually noisy and semantically odd.
    if (node.type === 'code' || node.type === 'pre' || node.type === 'a' || node.type === 'mark') {
      return node;
    }
    return cloneElement(node, undefined, markify(node.props.children, phrases));
  }

  return node;
}
